define('viewModels/privileged/composer/forms/formEditor/BaseQuestion',[
    'jquery',
    'lib/knockout',
    'lib/lodash4',
    'models/composer/forms/QuestionType',
    'viewModels/ViewModel',
    'viewModels/privileged/composer/vmUtils',
    'services/toastService',
    'services/composer/templateService',
    'services/loggingService'
], function (
    $,
    ko,
    _,
    QuestionType,
    ViewModel,
    utils,
    toastService,
    templateService,
    loggingService
) {
    "use strict";

    var NA_VALUE = "N/A";

    var BaseQuestionViewModel = ViewModel.extend(function baseQuestionVm_init(questionModel, addQuestionCallback, formEditorState, questionMap) {
        var self = this;

        self.id = questionModel.id;

        // All question types.  This is necessary to allow us to import the correct template based on type
        self.QuestionType = QuestionType;

        // Observable containing current answer
        self.answer = questionModel.answer;

        // Observable containing current comments
        self.comments = questionModel.comments;

        // Observable flag to determine if answering the question is disabled due to N/A being selected
        self.naAnsweringDisabled = ko.observable(false);

        // Observable flag to determine if this question has been marked as N/A
        self.naSelected = ko.observable(false);

        //Only applies to multiple choice questions, defined here for uniformity
        self.selectDefaultAnswer = _.noop;

        //Adds Mode, isEditMode, isEvaluatorActionMode, etc.
        _.extend(self, formEditorState.modes);

        // Flag to determine if this question is N/A-enabled
        self.isNaEnabled = formEditorState.trackModified(self, questionModel.naEnabled);

        self.shouldShowSubmitErrors = formEditorState.shouldShowSubmitErrors;

        self.visibilityConditionVisible = ko.observable(true);

        self.visible = self.computed(function () {
            return self.isEditMode() ? true : self.visibilityConditionVisible();
        });

        self.canEnableVisibilityCondition = function () {
            // overridden by implementations of question
            return false;
        };

        self.validateCanMoveUp = function (entityModelList) {
            // overridden by implementations of question
            var index = questionModel.index();
            return (index >= 1 && index <= (entityModelList().length - 1));
        };

        self.validateCanMoveDown = function (entityModelList) {
            // overridden by implementations of question
            var index = questionModel.index();
            return (index >= 0 && index <= (entityModelList().length - 2));
        };

        // Flag to determine if this question is N/A-enabled
        self.commentsRequired = formEditorState.trackModified(self, questionModel.commentsRequired);

        self.commentsWatermark = self.computed(function () {
            if (self.commentsRequired()) {
                return ko.i18n.renderString('composer.evaluations.question.commentsRequiredWatermark');
            }
            else {
                return ko.i18n.renderString('composer.evaluations.question.commentsWatermark');
            }
        });

        self.commentsFieldValid = self.computed(function () {
            return !self.commentsRequired() || self.comments();
        });

        self.hasStatus = self.computed(function () {
            return self.isNaEnabled() ||
                   self.commentsRequired();
        });

        //When naSelected changes, call self.onNASelected (in a way that allows it to be overridden)
        var naSelectedSubscription = self.naSelected.subscribe(_.bindKey(self, "onNASelected"));

        //Can be overriden by implementing classes
        var naLastAnswer = '';
        self.onNASelected = function baseNASelected(naSelected) {
            if (naSelected) {
                naLastAnswer = self.answer();
                self.answer(NA_VALUE);
                self.naAnsweringDisabled(true);
                return;
            }
            self.answer(naLastAnswer);
            self.naAnsweringDisabled(false);
        };

        //When isPreviewMode changes, call self.onPreviewModeChange (in a way that allows it to be overridden)
        var previewModeSubscription = self.isPreviewMode.subscribe(_.bindKey(self, "onPreviewModeChange"));

        self.onPreviewModeChange = function basePreviewModeChange(previewMode) {
            if(!previewMode) {
                self.clearResponse();
                self.comments('');
            }
        };

        // Resets the question to initial unanswered state
        self.clearResponse = function () {
            self.naSelected(false);
            self.naLastAnswer = '';
            self.answer('');
        };

        self.index = self.computed(function () {
            return questionModel.index();
        });

        self.questionGroupIndex = self.computed(function () {
            return questionModel.questionGroupIndex();
        });
        
        self.getQuestionOptions = self.computed(function () {
            var index = questionModel.index();
            var questionGroupIndex = questionModel.questionGroupIndex();
            return _.filter(questionMap(), function (question) {
                return question.questionGroupIndex() < questionGroupIndex ||
                       question.questionGroupIndex() === questionGroupIndex && question.index() < index;
            });
        });

        // Flag to determine if this question is currently selected
        self.isSelected = self.computed(function baseQuestionVm_isSelected() {
            return formEditorState.selectedItem() === _id;
        });

        self.isFocused = self.computed(function baseQuestionVm_isFocused() {
            return formEditorState.selectedItem() === _id;
        });

        // The id of the question
        var _id = questionModel.modelId;
        self.id = questionModel.id || _id;

        // The question type
        self.type = questionModel.type;

        // The user defined text of the question
        self.text = formEditorState.trackModified(self, questionModel.text);

        //Computed list of design-time validation errors associated with the text field
        self.textErrors = utils.modelErrors(self, questionModel.text);

        // The help text
        self.helpText = formEditorState.trackModified(self, questionModel.helpText);

        self.triggerSelect = function (vm, e) {
            var evt = e.originalEvent || e; //originalEvent, if we've hit a jQuery callback at some point
            if(!evt.ccPreventDeselect) {
                self.select();
                evt.ccPreventDeselect = true;
            }
            // NOTE: This must be here to allow checkboxes (etc) to be checked
            return true;
        };

        // Selects this question in the form editor
        self.select = function () {
            if (self.isEditMode()) {
                formEditorState.selectedItem(_id);
            }
        };

        // Unselects this questions in the form editor
        self.unselect = function (vm, e) {
            formEditorState.selectedItem(null);
            e.stopPropagation();
            e.cancelBubble = true;
        };

        //computed that controls the hoverable css class for hover behavior
        self.canHover = self.computed(function () {
            return !formEditorState.selectedItem();
        });

        self.savingTemplate = ko.observable(false);
        self.templateName = ko.observable();
        self.saveTemplate = function () {
            var templateName = self.templateName();
            if(templateName) {
                if(!templateName.trim()){
                    //Prevent template names that are entirely whitespace
                    toastService.notifyMessage(
                        'composer.evaluations.editor.templates.save.failure.title',
                        {
                            bodyMessageKey: 'composer.evaluations.editor.templates.save.failure.message.allWhitespace'
                        }
                    );
                    return;
                }
                var templateData = questionModel.unwrap({
                    'baseQuestion': ['id'],
                    'multipleChoiceQuestionAnswer': ['id']
                }, { ignoreFakeIds: true });
                self.savingTemplate(true);
                var template = {
                    name: templateName,
                    data: templateData,

                    tags: ["question"]
                };
                templateService.createUserTemplate(templateService.EVALUATIONS_SERVICE, template)
                .then(function (template) {
                    toastService.notifyMessage(
                        'composer.evaluations.editor.templateSave.success.title',
                        {
                            bodyMessageKey: 'composer.evaluations.editor.templateSave.success.message',
                            messageData: { name: templateName },
                            icon: "fa-check-circle"
                        }
                    );
                    formEditorState.questionTemplates.push(template);
                    self.savingTemplate(false);
                })
                .catch(function () {
                    toastService.notifyMessage(
                        'composer.evaluations.editor.templates.save.failure.title',
                        {
                            bodyMessageKey: 'composer.evaluations.editor.templates.save.failure.message'
                        }
                    );
                    self.savingTemplate(false);
                });
            }
        };

        self.addQuestionAbove = function (data) {
            addQuestionCallback(data, self, addQuestionCallback.ABOVE);
        };

        self.addQuestionBelow = function (data) {
            addQuestionCallback(data, self, addQuestionCallback.BELOW);
        };

        self.questionBarIsSelectedCallback = function (location) { //"TOP" or "BOTTOM" currently
            return self.computed(function() {
                return formEditorState.selectedAddQuestionBar() === _id + location;
            });
        };

        self.questionBarSelectCallback = function (location) {
            return function() {
                return formEditorState.selectedAddQuestionBar(_id + location);
            };
        };

        self.questionBarClickHandler = function () {
            return function(vm, e) {
                var evt = e.originalEvent || e; //originalEvent, if we've hit a jQuery callback at some point
                evt.ccPreventDeselectAddQuestionBar = true;
                // NOTE: This must be here to allow checkboxes (etc) to be checked
                return true;
            };
        };

        // Question validations (pre-submission)- returns an array of validation errors.
        self.validationsForSubmission = self.computed(function baseQuestionVm_validationsForSubmission() {
            var errors = [];
            // Required Validation Handling
            if (self.visibilityConditionVisible()) {
                if (!self.answer()) {
                    errors.push({
                        question: self,
                        message: 'composer.evaluations.editor.submitValidations.errors.notAnswered'
                    });
                }
                // Required Validation Handling
                if (!self.commentsFieldValid()) {
                    errors.push({
                        question: self,
                        message: 'composer.evaluations.editor.submitValidations.errors.commentsFieldInvalid'
                    });
                }
            }
            return errors;
        });

        // Get the answers in a form that quality can use them.  Transform attribute names
        self.getQualityAnswerData = function() {
            return {
                questionId: questionModel.id,
                answer: questionModel.answer(),
                markedNA: questionModel.naSelected(),
                comments: questionModel.comments()
            };

        };

        self.fillQualityAnswerData = function(qualityQuestionAnswerData) {
            if (qualityQuestionAnswerData) {
                if (qualityQuestionAnswerData.markedNA) {
                    if (!self.isNaEnabled()) {
                        loggingService.error("Attempted to select NA for a question that isn't NA enabled");
                    } else {
                        self.naSelected(true);
                        self.answer(NA_VALUE);
                    }
                } else {
                    self.naSelected(false);
                    self.answer(qualityQuestionAnswerData.answer);
                }
                self.comments(qualityQuestionAnswerData.comments);
            }
        };

        // disposes subscription to isPreviewMode
        self.onDispose = function () {
            naSelectedSubscription.dispose();
            previewModeSubscription.dispose();
        };

    });

    return BaseQuestionViewModel;
});

